// Regras puras do painel "Balanceamento por peso" (Roteamento de Pagamentos, modo 1).
// A validação espelha domain.ValidateRoutingConfig do ERPApi (que é a trava de verdade) e
// ValidateWeightedConfig do PaymentHubGo: pelo menos uma empresa ativa sem teto, peso inteiro
// de 1 a 100 em toda empresa ativa, e toda transportadora bloqueada aceita por alguma ativa.

import type { CarrierServiceOption } from '@/types/paymentRouting';
import { centsToInput, toCents } from '@pages/financial/installmentCalc';

export const MIN_WEIGHT = 1;
export const MAX_WEIGHT = 100;

export interface WeightedRuleInput {
  company_id: number;
  company_name: string;
  enabled: boolean;
  weight: number | null;
  /** Teto mensal em centavos; null = sem teto. */
  cap_cents: number | null;
  city_count: number;
  carrier_blocks: { provider_id: number; provider_service_id: number | null }[];
}

export interface WeightedValidation {
  errors: string[];
  warnings: string[];
}

const blockKey = (b: { provider_id: number; provider_service_id: number | null }) =>
  `${b.provider_id}:${b.provider_service_id ?? '*'}`;

/** O par (provider, serviço) é bloqueado pela lista? (null = todos os serviços). */
const blocksPair = (
  blocks: WeightedRuleInput['carrier_blocks'],
  providerId: number,
  serviceId: number | null,
): boolean =>
  blocks.some(
    b => b.provider_id === providerId && (b.provider_service_id == null || b.provider_service_id === serviceId),
  );

export const isValidWeight = (weight: number | null | undefined): boolean =>
  weight != null && Number.isInteger(weight) && weight >= MIN_WEIGHT && weight <= MAX_WEIGHT;

export const validateWeightedRules = (
  rules: readonly WeightedRuleInput[],
  labelOf: (providerId: number, serviceId: number | null) => string = (p, s) =>
    s == null ? `transportadora #${p} (todos os serviços)` : `transportadora #${p} · serviço #${s}`,
): WeightedValidation => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const active = rules.filter(r => r.enabled);

  if (active.length === 0) {
    errors.push('Ative pelo menos uma empresa no balanceamento.');
    return { errors, warnings };
  }

  if (!active.some(r => r.cap_cents == null)) {
    errors.push(
      'Pelo menos uma empresa ativa precisa ficar sem teto — sem ela, os pagamentos não têm para onde ir quando todas atingirem o teto.',
    );
  }

  for (const r of active) {
    if (!isValidWeight(r.weight)) {
      errors.push(`${r.company_name}: o peso deve ser um número inteiro de ${MIN_WEIGHT} a ${MAX_WEIGHT}.`);
    }
    if (r.cap_cents != null && r.cap_cents <= 0) {
      errors.push(`${r.company_name}: o teto mensal deve ser maior que zero (deixe vazio para "sem teto").`);
    }
    if (r.city_count > 0) {
      warnings.push(
        `${r.company_name} tem cidades restritas: pedidos de outras cidades nunca vão para ela, mesmo com peso.`,
      );
    }
  }

  // Cada bloqueio configurado precisa ser aceito por alguma empresa ativa.
  const seen = new Set<string>();
  const uncapped = active.filter(r => r.cap_cents == null);
  for (const r of rules) {
    for (const b of r.carrier_blocks) {
      const key = blockKey(b);
      if (seen.has(key)) continue;
      seen.add(key);
      const label = labelOf(b.provider_id, b.provider_service_id);
      if (active.every(a => blocksPair(a.carrier_blocks, b.provider_id, b.provider_service_id))) {
        errors.push(`Nenhuma empresa ativa aceita ${label}: pelo menos uma precisa ficar sem esse bloqueio.`);
      } else if (
        uncapped.length > 0 &&
        uncapped.every(a => blocksPair(a.carrier_blocks, b.provider_id, b.provider_service_id))
      ) {
        warnings.push(
          `Todas as empresas sem teto bloqueiam ${label}: esses pedidos vão para uma empresa com teto e podem ultrapassá-lo.`,
        );
      }
    }
  }

  return { errors, warnings };
};

/** Soma dos pesos válidos das empresas ativas. */
export const totalActiveWeight = (rules: readonly WeightedRuleInput[]): number =>
  rules.reduce((sum, r) => (r.enabled && isValidWeight(r.weight) ? sum + (r.weight as number) : sum), 0);

/** "2 de 3 = 67%" — participação alvo pelo peso. */
export const formatShare = (weight: number | null, total: number): string => {
  if (!isValidWeight(weight) || total <= 0) return '—';
  const pct = Math.round(((weight as number) / total) * 100);
  return `${weight} de ${total} = ${pct}%`;
};

export interface MonthMeterRow {
  company_id: number;
  captured: number;
  /** % do capturado do mês entre as empresas ativas. */
  actual_pct: number;
  /** % alvo pelo peso. */
  target_pct: number;
  /** Saldo do teto em reais; null = sem teto. */
  cap_remaining: number | null;
  /** % do teto consumido; null = sem teto. */
  cap_used_pct: number | null;
}

/** Medidor do mês: capturado real × alvo do peso, e saldo do teto. */
export const buildMonthMeter = (
  rules: readonly (WeightedRuleInput & { captured: number })[],
): MonthMeterRow[] => {
  const active = rules.filter(r => r.enabled);
  const capturedTotal = active.reduce((s, r) => s + Math.max(0, r.captured), 0);
  const weightTotal = totalActiveWeight(rules);
  return rules.map(r => {
    const captured = Math.max(0, r.captured);
    const cap = r.cap_cents != null ? r.cap_cents / 100 : null;
    return {
      company_id: r.company_id,
      captured,
      actual_pct: r.enabled && capturedTotal > 0 ? (captured / capturedTotal) * 100 : 0,
      target_pct: r.enabled && weightTotal > 0 && isValidWeight(r.weight) ? ((r.weight as number) / weightTotal) * 100 : 0,
      cap_remaining: cap != null ? Math.max(0, cap - captured) : null,
      cap_used_pct: cap != null && cap > 0 ? Math.min(100, (captured / cap) * 100) : null,
    };
  });
};

export interface CarrierProviderGroup {
  provider_id: number;
  provider_name: string;
  services: CarrierServiceOption[];
}

/** Agrupa a lista de transportadoras/serviços por transportadora, na ordem de nome. */
export const groupCarrierServices = (options: readonly CarrierServiceOption[]): CarrierProviderGroup[] => {
  const map = new Map<number, CarrierProviderGroup>();
  for (const o of options) {
    let g = map.get(o.provider_id);
    if (!g) {
      g = { provider_id: o.provider_id, provider_name: o.provider_name, services: [] };
      map.set(o.provider_id, g);
    }
    g.services.push(o);
  }
  const groups = Array.from(map.values());
  groups.forEach(g => g.services.sort((a, b) => a.provider_service_name.localeCompare(b.provider_service_name, 'pt-BR')));
  return groups.sort((a, b) => a.provider_name.localeCompare(b.provider_name, 'pt-BR'));
};

/** Rótulo legível de um par (provider, serviço) a partir da lista de serviços. */
export const carrierLabel = (
  options: readonly CarrierServiceOption[],
  providerId: number,
  serviceId: number | null,
): string => {
  const providerName = options.find(o => o.provider_id === providerId)?.provider_name ?? `Transportadora #${providerId}`;
  if (serviceId == null) return `${providerName} (todos os serviços)`;
  const svc = options.find(o => o.provider_id === providerId && o.provider_service_id === serviceId);
  return `${providerName} · ${svc?.provider_service_name ?? `serviço #${serviceId}`}`;
};

/** Liga/desliga um bloqueio na lista (imutável). */
export const toggleCarrierBlock = (
  blocks: readonly { provider_id: number; provider_service_id: number | null }[],
  providerId: number,
  serviceId: number | null,
): { provider_id: number; provider_service_id: number | null }[] => {
  const exists = blocks.some(b => b.provider_id === providerId && b.provider_service_id === serviceId);
  if (exists) return blocks.filter(b => !(b.provider_id === providerId && b.provider_service_id === serviceId));
  // "Todos os serviços" substitui os bloqueios por serviço da mesma transportadora.
  if (serviceId == null) {
    return [...blocks.filter(b => b.provider_id !== providerId), { provider_id: providerId, provider_service_id: null }];
  }
  return [...blocks, { provider_id: providerId, provider_service_id: serviceId }];
};

/**
 * Máscara de centavos para o teto: o usuário digita só números ("18000000" → R$ 180.000,00).
 * Devolve o valor no formato do rascunho (`centsToInput`, "180000.00"), ou '' = sem teto.
 */
export const capInputFromTyped = (typed: string): string => {
  const digits = typed.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (digits === '') return '';
  return centsToInput(Number(digits));
};

/** Exibe o rascunho do teto ("180000.00") como "180.000,00"; '' continua vazio. */
export const capInputDisplay = (draft: string): string => {
  const cents = draft.trim() === '' ? null : toCents(draft);
  if (cents == null) return '';
  return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Rascunho do teto → centavos (null = sem teto). */
export const capDraftToCents = (draft: string): number | null =>
  draft.trim() === '' ? null : toCents(draft);

/** Rascunho do peso → inteiro ou null. */
export const weightDraftToNumber = (draft: string): number | null => {
  const t = draft.trim();
  if (t === '' || !/^\d+$/.test(t)) return t === '' ? null : Number.NaN;
  return Number(t);
};

/** Campos da regra que o painel lê/edita (subconjunto do rascunho da aba). */
export interface WeightedDraftRule {
  company_id: number;
  company_name: string;
  company_document: string;
  enabled: boolean;
  weight: string;
  monthly_revenue_cap: string; // formato centsToInput; '' = sem teto
  cap_trigger_percent: number;
  captured_this_month: number;
  cities: { city_name: string; state_uf: string | null }[];
  carrier_blocks: { provider_id: number; provider_service_id: number | null }[];
}

/** Converte o rascunho para a entrada das regras puras (validação e medidor). */
export const toWeightedInput = (r: WeightedDraftRule): WeightedRuleInput => {
  const capCents = capDraftToCents(r.monthly_revenue_cap);
  const trigger = r.cap_trigger_percent > 0 && r.cap_trigger_percent <= 100 ? r.cap_trigger_percent : 100;
  const weight = weightDraftToNumber(r.weight);
  return {
    company_id: r.company_id,
    company_name: r.company_name,
    enabled: r.enabled,
    weight, // NaN (digitação inválida) reprova em isValidWeight
    // Teto efetivo = teto × % de disparo — o mesmo que o PaymentHub aplica.
    cap_cents: capCents == null ? null : Math.round((capCents * trigger) / 100),
    city_count: r.cities.length,
    carrier_blocks: r.carrier_blocks,
  };
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Hoje (relógio local) em ISO YYYY-MM-DD, sem passar por toISOString (que é UTC). */
export const todayISO = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;

/** Primeiro dia do mês corrente em ISO YYYY-MM-DD. */
export const firstDayOfMonthISO = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-01`;
